import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import { API_URL, normalizeList } from '../config';
import { logout } from '../features/auth/authSlice';
import './MyWalletPage.css';

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatPoints = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const MyWalletPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const [report, setReport] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [redemptionConfig, setRedemptionConfig] = useState({ pointsPerRupee: 10 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('report');
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [pointsToRedeem, setPointsToRedeem] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const [copiedCode, setCopiedCode] = useState('');

  const pageSize = 25;

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/Points/wallet-report?page=${page}&pageSize=${pageSize}`;
      if (typeFilter === 'Credit' || typeFilter === 'Debit') url += `&type=${typeFilter}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      const data = res.data || {};
      setReport({
        ...data,
        transactions: normalizeList(data.transactions),
      });
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        navigate('/login');
        return;
      }
      console.error('Error fetching wallet report:', err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate, page, typeFilter]);

  const fetchVouchers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/Points/my-vouchers`, { headers: getAuthHeader() });
      setVouchers(normalizeList(res.data));
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        navigate('/login');
        return;
      }
      console.error('Error fetching vouchers:', err);
    }
  }, [dispatch, navigate]);

  const fetchRedemptionConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/Points/redemption-config`);
      setRedemptionConfig({ pointsPerRupee: res.data?.pointsPerRupee ?? 10 });
    } catch {
      // use default
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    if (userRole && userRole !== 'Customer') {
      navigate('/');
    }
  }, [isLoggedIn, userRole, navigate]);

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Customer') {
      return;
    }

    fetchRedemptionConfig();

    if (activeTab === 'report') {
      fetchReport();
    } else if (activeTab === 'vouchers') {
      fetchVouchers();
    }
  }, [activeTab, fetchRedemptionConfig, fetchReport, fetchVouchers, isLoggedIn, userRole]);

  const handleRedeem = async (e) => {
    e.preventDefault();
    setRedeemError('');
    setRedeemSuccess(null);
    const points = parseInt(pointsToRedeem, 10);
    if (!points || points <= 0) {
      setRedeemError('Enter a valid number of points.');
      return;
    }
    const balance = report?.balance?.currentBalance ?? 0;
    if (points > balance) {
      setRedeemError('Insufficient wallet balance.');
      return;
    }
    setRedeemLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/Points/redeem`,
        { pointsToRedeem: points },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      setRedeemSuccess(res.data);
      setPointsToRedeem('');
      fetchReport();
      fetchVouchers();
    } catch (err) {
      const msg = err.response?.data?.message ?? err.response?.data ?? err.message;
      setRedeemError(typeof msg === 'string' ? msg : 'Redemption failed.');
    } finally {
      setRedeemLoading(false);
    }
  };

  const copyCode = (code) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 2000);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const balance = report?.balance;
  const transactions = report?.transactions ?? [];
  const pagination = report?.pagination;
  const pointsPerRupee = redemptionConfig.pointsPerRupee || 10;
  const discountPreview = pointsToRedeem && parseInt(pointsToRedeem, 10) > 0
    ? (parseInt(pointsToRedeem, 10) / pointsPerRupee).toFixed(2)
    : '0.00';

  return (
    <div className="wallet-page">
      <Header />
      <div className="wallet-container">
        <h1 className="wallet-page-title">My Wallet</h1>

        {/* Balance summary */}
        <div className="wallet-summary">
          <div className="wallet-card balance-card">
            <div className="wallet-card-icon">💰</div>
            <div className="wallet-card-content">
              <span className="wallet-card-label">Current Balance</span>
              <span className="wallet-card-value">{formatPoints(balance?.currentBalance)}</span>
              <span className="wallet-card-sub">points</span>
            </div>
          </div>
          <div className="wallet-card credits-card">
            <div className="wallet-card-icon">📈</div>
            <div className="wallet-card-content">
              <span className="wallet-card-label">Total Credits</span>
              <span className="wallet-card-value">{formatPoints(balance?.totalCredits)}</span>
            </div>
          </div>
          <div className="wallet-card debits-card">
            <div className="wallet-card-icon">📉</div>
            <div className="wallet-card-content">
              <span className="wallet-card-label">Total Debits</span>
              <span className="wallet-card-value">{formatPoints(Math.abs(Number(balance?.totalDebits ?? 0)))}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="wallet-tabs">
          <button
            type="button"
            className={`wallet-tab ${activeTab === 'report' ? 'active' : ''}`}
            onClick={() => setActiveTab('report')}
          >
            Debit / Credit Report
          </button>
          <button
            type="button"
            className={`wallet-tab ${activeTab === 'redeem' ? 'active' : ''}`}
            onClick={() => setActiveTab('redeem')}
          >
            Redeem
          </button>
          <button
            type="button"
            className={`wallet-tab ${activeTab === 'vouchers' ? 'active' : ''}`}
            onClick={() => setActiveTab('vouchers')}
          >
            My Vouchers
          </button>
        </div>

        <div className="wallet-tab-content">
          {activeTab === 'report' && (
            <div className="wallet-report-section">
              <div className="report-header">
                <h2>Transaction Report</h2>
                <select
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                  className="wallet-filter-select"
                >
                  <option value="">All</option>
                  <option value="Credit">Credit</option>
                  <option value="Debit">Debit</option>
                </select>
              </div>
              {loading && !report ? (
                <div className="wallet-loading">Loading report...</div>
              ) : (
                <>
                  <div className="wallet-table-wrap">
                    <table className="wallet-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Amount</th>
                          <th>Balance After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="no-data-cell">No transactions yet.</td>
                          </tr>
                        ) : (
                          transactions.map((tx) => (
                            <tr key={tx.transactionId}>
                              <td>{formatDate(tx.date)}</td>
                              <td>
                                <span className={`tx-type-badge ${tx.type === 'Credit' ? 'credit' : 'debit'}`}>
                                  {tx.type}
                                </span>
                              </td>
                              <td>{tx.description || '—'}</td>
                              <td className={tx.amount >= 0 ? 'amount-credit' : 'amount-debit'}>
                                {Number(tx.amount) >= 0 ? '+' : ''}{formatPoints(tx.amount)}
                              </td>
                              <td>{tx.balanceAfter != null ? formatPoints(tx.balanceAfter) : '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {pagination && pagination.totalPages > 1 && (
                    <div className="wallet-pagination">
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        Previous
                      </button>
                      <span>Page {page} of {pagination.totalPages}</span>
                      <button
                        type="button"
                        disabled={page >= pagination.totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'redeem' && (
            <div className="wallet-redeem-section">
              <h2>Redeem Points for Voucher</h2>
              <p className="redeem-info">
                {pointsPerRupee} points = ₹1 discount. Use the voucher code at checkout.
              </p>
              <form onSubmit={handleRedeem} className="redeem-form">
                <div className="redeem-field">
                  <label htmlFor="points-redeem">Points to redeem</label>
                  <input
                    id="points-redeem"
                    type="number"
                    min="1"
                    max={balance?.currentBalance ?? 0}
                    value={pointsToRedeem}
                    onChange={(e) => setPointsToRedeem(e.target.value)}
                    placeholder="e.g. 100"
                  />
                  <span className="redeem-preview">≈ ₹{discountPreview} discount</span>
                </div>
                {redeemError && <div className="redeem-error">{redeemError}</div>}
                {redeemSuccess && (
                  <div className="redeem-success-box">
                    <p>{redeemSuccess.message}</p>
                    <p><strong>Voucher code:</strong> <code>{redeemSuccess.voucherCode}</code></p>
                    <p>Discount: ₹{Number(redeemSuccess.discountAmount).toFixed(2)} · Expires: {formatDate(redeemSuccess.expiresAt)}</p>
                    <button
                      type="button"
                      className="copy-code-btn"
                      onClick={() => copyCode(redeemSuccess.voucherCode)}
                    >
                      {copiedCode === redeemSuccess.voucherCode ? 'Copied!' : 'Copy code'}
                    </button>
                  </div>
                )}
                <button
                  type="submit"
                  className="redeem-submit"
                  disabled={redeemLoading || !pointsToRedeem || (balance?.currentBalance ?? 0) < 1}
                >
                  {redeemLoading ? 'Redeeming...' : 'Redeem'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'vouchers' && (
            <div className="wallet-vouchers-section">
              <h2>My Vouchers</h2>
              <p className="vouchers-hint">Unused vouchers. Apply at checkout.</p>
              {vouchers.length === 0 ? (
                <div className="no-data">No vouchers yet. Redeem points to get a voucher.</div>
              ) : (
                <div className="vouchers-list">
                  {vouchers.map((v) => (
                    <div key={v.id} className="voucher-card">
                      <div className="voucher-code-row">
                        <code className="voucher-code">{v.code}</code>
                        <button
                          type="button"
                          className="copy-code-btn small"
                          onClick={() => copyCode(v.code)}
                        >
                          {copiedCode === v.code ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <div className="voucher-meta">
                        <span>₹{Number(v.discountAmount).toFixed(2)} off</span>
                        <span>Expires: {formatDate(v.expiresAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MyWalletPage;
